import { useGradeLevelsChangesContext } from "../context/GradeLevelsChangesContext";
import useStudentState from "./useStudentState";

const defaultMap = new Map();
const defaultSet = new Set();

/**
 * Hook for managing all grade level changes for a specific student
 * Provides student-specific state and setters for updated, removed, added, and errors
 *
 * @param {string} studentId - The ID of the student
 * @returns {Object} Object containing:
 *  - updated: Map of updated grades for the student
 *  - setUpdated: Setter function for updates
 *  - removed: Set of removed grade IDs
 *  - setRemoved: Setter function for removed items
 *  - added: Map of added grades
 *  - setAdded: Setter function for added items
 *  - errors: Map of validation errors
 *  - setErrors: Setter function for errors
 */
const useStudentGradeLevelChanges = studentId => {
  const {
    allUpdated,
    setAllUpdated,
    allRemoved,
    setAllRemoved,
    allAdded,
    setAllAdded,
    allErrors,
    setAllErrors,
  } = useGradeLevelsChangesContext();

  const [updated, setUpdated] = useStudentState(
    studentId,
    allUpdated,
    setAllUpdated,
    defaultMap
  );

  const [removed, setRemoved] = useStudentState(
    studentId,
    allRemoved,
    setAllRemoved,
    defaultSet
  );

  const [added, setAdded] = useStudentState(
    studentId,
    allAdded,
    setAllAdded,
    defaultMap
  );

  const [errors, setErrors] = useStudentState(
    studentId,
    allErrors,
    setAllErrors,
    defaultMap
  );

  return {
    updated,
    setUpdated,
    removed,
    setRemoved,
    added,
    setAdded,
    errors,
    setErrors,
  };
};

export default useStudentGradeLevelChanges;
